import React, { useEffect, useRef, useState } from 'react';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import styles from '@/components/staffer/Grading/QuestionCards/questionCard.module.scss';
import { IQuestion } from '@/components/staffer/Grading/types';
import GradingView from '@/components/common/Forms/Questions/DragAndDrop/Answer/GradingView';
import SlideshowImage from 'modules/SlideshowImage';
import { useQuestionRubric } from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import clsx from 'clsx';
import RubricPreview from '@/components/staffer/ExerciseBuilder/Rubric/EditRubricCard/RubricPreview';

type RubricData = ReturnType<typeof useQuestionRubric>['data'];

interface AnswerKeyProps {
  answerKeyHtml?: string;
  question: IQuestion;
  rubricData?: RubricData;
}

const AnswerKey = ({ answerKeyHtml, question, rubricData } : AnswerKeyProps) => {
  const filters = useGradingStore(state => state.filters);
  const { answerKeyFilter } = filters;
  const [showAnswerKey, setShowAnswerKey] = useState(answerKeyFilter === 'show');
  const toggleAnswerKey = () => setShowAnswerKey(!showAnswerKey);
  const answerKeyContainerRef = useRef<HTMLDivElement>(null);

  const dragAndDrop = question.type === 'DragAndDropQuestion';

  useEffect(() => {
    setShowAnswerKey(answerKeyFilter === 'show');
  }, [answerKeyFilter]);

  useEffect(() => {
    if (!answerKeyContainerRef.current || !answerKeyHtml || dragAndDrop) return;

    const imgElements = answerKeyContainerRef.current.querySelectorAll('img[data-zoomurl]');
    imgElements.forEach((img) => {
      const slideshowImage = new SlideshowImage();
      slideshowImage.init(img as HTMLImageElement);
    });
  }, [answerKeyHtml, dragAndDrop]);

  const hasRubric = !!rubricData;

  const answerKey = () => {
    if (dragAndDrop) {
      return (
        <div className={styles.dndAnswerContainer} id={`drag-and-drop-answer-key-${question.id}`}>
          <GradingView locale="en" parentContainerId={`drag-and-drop-answer-key-${question.id}`} question={question} />
        </div>
      );
    }

    if (!answerKeyHtml && !hasRubric) {
      return (<div className="respect_breaks allow-list-style"> No answer provided. </div>);
    }

    return (
      <div>
        {answerKeyHtml && (
        <div
          ref={answerKeyContainerRef}
          className={clsx(styles.questionAnswerKeyContainer, 'tw-mb-4')}
          dangerouslySetInnerHTML={{ __html: answerKeyHtml }}
        />
        )}
        {hasRubric && (
          <RubricPreview
            analyticRubricProps={{
              classes: { cell: 'tw-h-[5rem]', container: 'tw-mt-2' },
              hasHeaders: true,
            }}
            holisticRubricProps={{
              classes: { cell: 'tw-h-[5rem]', container: 'tw-mt-2' },
              hasHeaders: true,
            }}
            rubric={rubricData}
          />
        )}
      </div>
    );
  };

  return (
    <div className="tw-mt-2.5">
      <button
        className={styles.answerKeyToggle}
        type="button"
        onClick={toggleAnswerKey}
      >
        Answer Key <i aria-hidden className={`fa tw-ml-0.5 ${showAnswerKey ? 'fa-caret-down' : 'fa-caret-right'}`} />
      </button>
      {showAnswerKey && answerKey()}
    </div>
  );
};

export default AnswerKey;
